import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  ShiftSalesItem,
  ShiftStockCountItem,
  ShiftReconciliationResult,
} from '../types';
import { api } from '../api';
import { tg } from '../telegram';
import {
  Camera,
  CheckCircle2,
  RefreshCw,
  ArrowLeft,
  AlertTriangle,
  Plus,
  Trash2,
  Image as ImageIcon,
  ArrowRight,
  PenLine,
  Scale,
  Coffee,
  Sparkles,
  Layers,
  HelpCircle,
  Info,
  X,
} from 'lucide-react';
import { CameraCapture } from '../components/CameraCapture';
import { ConfirmView } from '../components/ui/ConfirmView';
import { ItemDropdown } from '../components/ui/ItemDropdown';
import { AddShiftSaleModal } from '../components/ui/AddShiftSaleModal';
import { AddShiftStockModal } from '../components/ui/AddShiftStockModal';
import { useSubmitAction } from '../hooks/useSubmitAction';
import { useMasterData } from '../hooks/useMasterData';
import { formatNumber, formatQty, formatVariance } from '../utils/format';
import { toComparisonKey } from '../utils/textNormalizer';
import toast from 'react-hot-toast';

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

export const ShiftReconcileScreen: React.FC<Props> = ({ onBack, onSuccess }) => {
  const { products, ingredients, boms, units } = useMasterData();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [selectedImages, setSelectedImages] = useState<Array<{ base64: string; mimeType: string }>>([]);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  // Active review sub-tab: 'SALES' | 'RAW' | 'PREP'
  const [activeTab, setActiveTab] = useState<'SALES' | 'RAW' | 'PREP'>('SALES');

  // Flow 3: 3 core groups of shift data
  // 1. Doanh số món bán từ POS
  const [sales, setSales] = useState<ShiftSalesItem[]>([]);
  // 2. Cân đo tồn Nguyên liệu thô (NL) cuối ca
  const [rawStock, setRawStock] = useState<ShiftStockCountItem[]>([]);
  // 3. Cân đo tồn Bán thành phẩm đã pha (BTP) cuối ca
  const [preparedStock, setPreparedStock] = useState<ShiftStockCountItem[]>([]);
  // 4. Ngưỡng chênh lệch tài chính chống thất thoát / gian lận (VNĐ)
  const [financialThreshold, setFinancialThreshold] = useState<number | ''>(25000);
  const [showThresholdHelp, setShowThresholdHelp] = useState(false);

  // Tự động lấy ngưỡng tài chính từ cấu hình BTP trong Master Data
  const masterThreshold = useMemo(() => {
    for (const b of boms || []) {
      if (b.note && b.note.toUpperCase().includes('SUBSTITUTE_MEMBER')) {
        const m = b.note.match(/THRESHOLD[:=]\s*(\d+)/i);
        if (m && !isNaN(Number(m[1]))) {
          return Number(m[1]);
        }
      }
    }
    return 25000;
  }, [boms]);

  useEffect(() => {
    if (masterThreshold) {
      setFinancialThreshold(masterThreshold);
    }
  }, [masterThreshold]);

  // Add Item Modals
  const [isAddSaleModalOpen, setIsAddSaleModalOpen] = useState(false);
  const [stockModalConfig, setStockModalConfig] = useState<{
    isOpen: boolean;
    type: 'RAW' | 'PREPARED';
  }>({ isOpen: false, type: 'RAW' });

  const handleAddSale = (item: { product_name: string; quantity: number }) => {
    setSales((prev) => [...prev, item]);
    toast.success(`Đã thêm món "${item.product_name}"`);
  };

  const handleAddStock = (item: {
    item_name: string;
    quantity: number;
    unit: string;
    type: 'RAW' | 'PREPARED';
  }) => {
    if (item.type === 'RAW') {
      setRawStock((prev) => [...prev, item]);
      toast.success(`Đã thêm "${item.item_name}" vào NL thô`);
    } else {
      setPreparedStock((prev) => [...prev, item]);
      toast.success(`Đã thêm "${item.item_name}" vào BTP`);
    }
  };

  // Reconciliation Result
  const [result, setResult] = useState<ShiftReconciliationResult | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter master data by type
  const rawMaster = ingredients.filter((i) => i.type !== 'PREPARED');
  const prepMaster = ingredients.filter((i) => i.type === 'PREPARED');

  // Helper deduplicate items (khử trùng lặp do ảnh dài chụp chồng mép nhau)
  const deduplicateLoadedItems = (
    salesList: ShiftSalesItem[],
    rawList: ShiftStockCountItem[],
    prepList: ShiftStockCountItem[]
  ) => {
    let totalMerged = 0;

    // 1. Sales (POS): gom theo tên món
    const salesMap = new Map<string, ShiftSalesItem>();
    for (const s of salesList) {
      if (!s.product_name.trim() || s.quantity <= 0) continue;
      const k = toComparisonKey(s.product_name);
      if (!salesMap.has(k)) {
        salesMap.set(k, { ...s });
      } else {
        totalMerged++;
        const existing = salesMap.get(k)!;
        existing.quantity = Math.max(existing.quantity, s.quantity);
        if (s.amount && existing.amount) existing.amount = Math.max(existing.amount, s.amount);
      }
    }

    // 2. Cân tồn NL thô: 1 số đếm duy nhất cho mỗi nguyên liệu trong ca
    const rawMap = new Map<string, ShiftStockCountItem>();
    for (const r of rawList) {
      if (!r.item_name.trim()) continue;
      const k = toComparisonKey(r.item_name);
      if (!rawMap.has(k)) {
        rawMap.set(k, { ...r });
      } else {
        totalMerged++;
        const existing = rawMap.get(k)!;
        if (existing.quantity === 0 && r.quantity > 0) existing.quantity = r.quantity;
      }
    }

    // 3. Cân tồn BTP đã pha: 1 số đếm duy nhất cho mỗi BTP trong ca
    const prepMap = new Map<string, ShiftStockCountItem>();
    for (const p of prepList) {
      if (!p.item_name.trim()) continue;
      const k = toComparisonKey(p.item_name);
      if (!prepMap.has(k)) {
        prepMap.set(k, { ...p });
      } else {
        totalMerged++;
        const existing = prepMap.get(k)!;
        if (existing.quantity === 0 && p.quantity > 0) existing.quantity = p.quantity;
      }
    }

    return {
      sales: Array.from(salesMap.values()),
      raw: Array.from(rawMap.values()),
      prep: Array.from(prepMap.values()),
      totalMerged,
    };
  };

  // Step 1: Upload or capture 1, 2 or 3 photos of bill POS / shift report -> OCR
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    tg.haptic.impact('medium');
    const newImgs: Array<{ base64: string; mimeType: string }> = [];

    const readers = Array.from(files).map((file) => {
      return new Promise<void>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          newImgs.push({
            base64: reader.result as string,
            mimeType: file.type || 'image/jpeg',
          });
          resolve();
        };
        reader.readAsDataURL(file);
      });
    });

    await Promise.all(readers);
    setSelectedImages((prev) => [...prev, ...newImgs].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = '';
    toast.success(`Đã thêm ${newImgs.length} ảnh vào danh sách!`);
  };

  const handleCameraCapture = async (base64Image: string) => {
    setSelectedImages((prev) => [...prev, { base64: base64Image, mimeType: 'image/jpeg' }].slice(0, 5));
    setIsCameraOpen(false);
    toast.success('Đã thêm 1 ảnh chụp!');
  };

  const removeSelectedImage = (index: number) => {
    tg.haptic.impact('light');
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const processImageOcr = async (imagesToProcess = selectedImages) => {
    if (imagesToProcess.length === 0) {
      toast.error('Vui lòng chọn hoặc chụp ít nhất 1 ảnh để phân tích');
      return;
    }

    setOcrLoading(true);
    const toastId = toast.loading(`AI Gemini đang phân tích & ghép nối ${imagesToProcess.length} ảnh...`);

    try {
      const ocrData = await api.scanShiftReportOcr(imagesToProcess);
      const itemsSales = ocrData.sales || [];
      const itemsRaw = ocrData.actual_raw_stock || [];
      const itemsPrep = ocrData.actual_prepared_stock || [];

      // Tự động khử trùng lặp dữ liệu từ ảnh chụp chồng lấn
      const deduped = deduplicateLoadedItems(itemsSales, itemsRaw, itemsPrep);

      setSales(deduped.sales);
      if (deduped.raw.length > 0) setRawStock(deduped.raw);
      if (deduped.prep.length > 0) setPreparedStock(deduped.prep);

      setStep(2); // Chuyển sang Bước 2 (Chạm sửa & Review)
      const mergeNote = deduped.totalMerged > 0 ? ` (đã lọc ${deduped.totalMerged} dòng trùng)` : '';
      toast.success(
        `Đã bóc tách ${deduped.sales.length} món bán, ${deduped.raw.length} NL thô, ${deduped.prep.length} BTP${mergeNote}!`,
        { id: toastId }
      );
      tg.haptic.success();
    } catch (err: any) {
      toast.error(`Không thể quét ảnh: ${err.message}. Bạn có thể nhập tay.`, { id: toastId });
      setStep(2);
    } finally {
      setOcrLoading(false);
    }
  };

  // Quick fill master items
  const populateAllRawIngredients = () => {
    tg.haptic.impact('light');
    const existingNames = new Set(rawStock.map((r) => r.item_name));
    const toAdd = rawMaster
      .filter((m) => !existingNames.has(m.name))
      .map((m) => ({ item_name: m.name, quantity: 0, unit: m.unit, type: 'RAW' as const }));

    if (toAdd.length === 0) {
      toast('Đã có đầy đủ danh mục NL thô');
      return;
    }
    setRawStock([...rawStock, ...toAdd]);
    toast.success(`Đã thêm ${toAdd.length} nguyên liệu thô vào danh sách kiểm kê`);
  };

  const populateAllPrepIngredients = () => {
    tg.haptic.impact('light');
    const existingNames = new Set(preparedStock.map((p) => p.item_name));
    const toAdd = prepMaster
      .filter((m) => !existingNames.has(m.name))
      .map((m) => ({ item_name: m.name, quantity: 0, unit: m.unit, type: 'PREPARED' as const }));

    if (toAdd.length === 0) {
      toast('Đã có đầy đủ danh mục BTP');
      return;
    }
    setPreparedStock([...preparedStock, ...toAdd]);
    toast.success(`Đã thêm ${toAdd.length} bán thành phẩm vào danh sách kiểm kê`);
  };

  // Submit action hook
  const { isSubmitting, isDisabled, execute: submitReconciliation } = useSubmitAction(
    async () => {
      const validSales = sales.filter((s) => s.product_name.trim() && s.quantity > 0);
      const validRaw = rawStock.filter((r) => r.item_name.trim() && r.quantity >= 0);
      const validPrep = preparedStock.filter((p) => p.item_name.trim() && p.quantity >= 0);

      const res = await api.reconcileShift({
        sales: validSales,
        actual_raw_stock: validRaw,
        actual_prepared_stock: validPrep,
        actual_stock: [...validRaw, ...validPrep],
        waste: [],
        auto_balance: true,
        loss_tolerance_pct: 10,
        financial_loss_threshold: typeof financialThreshold === 'number' && financialThreshold > 0 ? financialThreshold : undefined,
      });
      setResult(res);
    },
    {
      successMessage: 'Chốt ca & tính toán tiêu thụ nguyên liệu thành công!',
      onSuccess: () => {
        setStep(4);
      },
    }
  );

  const goToConfirm = () => {
    // Tự động loại bỏ trùng lặp trước khi sang màn hình xác nhận
    const deduped = deduplicateLoadedItems(sales, rawStock, preparedStock);
    setSales(deduped.sales);
    setRawStock(deduped.raw);
    setPreparedStock(deduped.prep);

    if (deduped.totalMerged > 0) {
      toast.success(`Đã tự động loại bỏ ${deduped.totalMerged} dòng bị trùng lặp giữa các ảnh!`);
    }

    const validSales = deduped.sales.filter((s) => s.product_name.trim() && s.quantity > 0);
    const validRaw = deduped.raw.filter((r) => r.item_name.trim());
    const validPrep = deduped.prep.filter((p) => p.item_name.trim());

    if (validSales.length === 0 && validRaw.length === 0 && validPrep.length === 0) {
      toast.error('Vui lòng nhập ít nhất 1 món bán hoặc 1 mặt hàng cân đo tồn.');
      return;
    }
    tg.haptic.impact('light');
    setStep(3);
  };

  interface ShiftConfirmRow {
    label: string;
    value: string;
    badge: string;
    sourceType: 'SALES' | 'RAW' | 'PREP';
    sourceKey: string;
    originalName: string;
  }

  // Build confirmation list with row-deletion capability
  const confirmItems: ShiftConfirmRow[] = [
    ...sales
      .filter((s) => s.product_name.trim() && s.quantity > 0)
      .map((s) => ({
        label: s.product_name,
        value: `${formatNumber(s.quantity)} ly`,
        badge: 'Doanh Số Bán (POS)',
        sourceType: 'SALES' as const,
        sourceKey: toComparisonKey(s.product_name),
        originalName: s.product_name,
      })),
    ...rawStock
      .filter((r) => r.item_name.trim())
      .map((r) => ({
        label: `[NL] ${r.item_name}`,
        value: formatQty(r.quantity, r.unit || 'g'),
        badge: 'Cân Tồn NL Thô',
        sourceType: 'RAW' as const,
        sourceKey: toComparisonKey(r.item_name),
        originalName: r.item_name,
      })),
    ...preparedStock
      .filter((p) => p.item_name.trim())
      .map((p) => ({
        label: `[BTP] ${p.item_name}`,
        value: formatQty(p.quantity, p.unit || 'ml'),
        badge: 'Cân Tồn BTP Đã Pha',
        sourceType: 'PREP' as const,
        sourceKey: toComparisonKey(p.item_name),
        originalName: p.item_name,
      })),
  ];

  // Xóa trực tiếp row ở màn hình confirm (Bước 3)
  const handleDeleteConfirmRow = (item: any) => {
    const row = item as ShiftConfirmRow;
    tg.haptic.impact('medium');
    if (row.sourceType === 'SALES') {
      setSales((prev) => prev.filter((s) => toComparisonKey(s.product_name) !== row.sourceKey));
      toast.success(`Đã xóa món bán "${row.originalName}"`);
    } else if (row.sourceType === 'RAW') {
      setRawStock((prev) => prev.filter((r) => toComparisonKey(r.item_name) !== row.sourceKey));
      toast.success(`Đã xóa NL thô "${row.originalName}"`);
    } else if (row.sourceType === 'PREP') {
      setPreparedStock((prev) => prev.filter((p) => toComparisonKey(p.item_name) !== row.sourceKey));
      toast.success(`Đã xóa BTP "${row.originalName}"`);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onBack} className="stepper-btn" title="Quay lại">
          <ArrowLeft size={18} />
        </button>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 16, fontWeight: 800 }}>Luồng 3: Chốt Tồn Ca & Doanh Số</h2>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            Cân đo NL & BTP tồn &rarr; Tính tổng gam NL tiêu thụ & Cảnh báo kho
          </span>
        </div>
        <div style={{ width: 36 }} />
      </div>

      {/* 4-Step Indicator */}
      <div className="flow-step-bar">
        <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>
          <span className="step-num">1</span> Gửi Ảnh
        </div>
        <div style={{ flex: 1, height: 2, background: step >= 2 ? 'var(--primary-accent)' : 'var(--border-color)' }} />
        <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>
          <span className="step-num">2</span> Chạm Sửa
        </div>
        <div style={{ flex: 1, height: 2, background: step >= 3 ? 'var(--primary-accent)' : 'var(--border-color)' }} />
        <div className={`step-indicator ${step >= 3 ? 'active' : ''}`}>
          <span className="step-num">3</span> Xác Nhận
        </div>
        <div style={{ flex: 1, height: 2, background: step >= 4 ? 'var(--primary-accent)' : 'var(--border-color)' }} />
        <div className={`step-indicator ${step >= 4 ? 'active' : ''}`}>
          <span className="step-num">4</span> Kết Quả
        </div>
      </div>

      {/* STEP 1: Upload Shift Report Image */}
      {step === 1 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: 16, fontWeight: 800 }}>Bóc Tách Phiếu Chốt Ca / Bill POS</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              Hỗ trợ chọn 1, 2 hoặc 3 ảnh liên tiếp (cho bill dài). AI sẽ tự động ghép nối và loại bỏ các mục bị chụp trùng mép.
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {/* Upload / Capture Action Buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div
              className="upload-zone"
              onClick={() => setIsCameraOpen(true)}
              style={{ minHeight: 110, padding: 14 }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'var(--primary-accent-subtle)',
                  color: 'var(--primary-accent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 8,
                }}
              >
                <Camera size={22} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 800 }}>Chụp Ảnh Trực Tiếp</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 2 }}>Chụp từng ảnh nối tiếp</div>
            </div>

            <div
              className="upload-zone"
              onClick={() => fileInputRef.current?.click()}
              style={{ minHeight: 110, padding: 14 }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'var(--primary-accent-subtle)',
                  color: 'var(--primary-accent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 8,
                }}
              >
                <ImageIcon size={22} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 800 }}>Chọn Từ Thư Viện</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 2 }}>Chọn 1, 2 hoặc 3 ảnh</div>
            </div>
          </div>

          {/* Selected Images Gallery Preview */}
          {selectedImages.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: 12,
                background: 'var(--bg-surface-subtle)',
                borderRadius: 'var(--radius-md)',
                border: '1.5px solid var(--border-color)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  📸 Danh sách {selectedImages.length} ảnh đã chọn ({selectedImages.length > 1 ? 'Ảnh nối tiếp' : '1 ảnh'}):
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedImages([])}
                  style={{
                    fontSize: 11,
                    color: 'var(--color-danger)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  Xóa hết
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 10 }}>
                {selectedImages.map((img, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: 'relative',
                      borderRadius: 'var(--radius-sm)',
                      overflow: 'hidden',
                      border: '1.5px solid var(--primary-accent)',
                      aspectRatio: '3/4',
                      background: '#000',
                    }}
                  >
                    <img
                      src={img.base64}
                      alt={`Ảnh ${idx + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        bottom: 3,
                        left: 3,
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        fontSize: 10,
                        padding: '1px 5px',
                        borderRadius: 3,
                        fontWeight: 700,
                      }}
                    >
                      Ảnh {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSelectedImage(idx)}
                      style={{
                        position: 'absolute',
                        top: 3,
                        right: 3,
                        background: 'rgba(239, 68, 68, 0.9)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: 22,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      title="Xóa ảnh này"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}

                {selectedImages.length < 5 && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      borderRadius: 'var(--radius-sm)',
                      border: '2px dashed var(--primary-accent)',
                      aspectRatio: '3/4',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      background: 'var(--bg-surface)',
                      gap: 4,
                    }}
                  >
                    <Plus size={22} color="var(--primary-accent)" />
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary-accent)', textAlign: 'center' }}>
                      + Thêm ảnh
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={() => processImageOcr()}
                disabled={ocrLoading}
                className="btn btn-primary btn-full"
                style={{
                  fontSize: 15,
                  minHeight: 48,
                  marginTop: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <Sparkles size={18} /> Bắt Đầu Phân Tích {selectedImages.length} Ảnh Bằng AI
              </button>
            </div>
          )}

          {ocrLoading && (
            <div
              style={{
                textAlign: 'center',
                padding: '20px 16px',
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.03) 100%)',
                borderRadius: 'var(--radius-md)',
                border: '1.5px solid rgba(245, 158, 11, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div
                className="animate-pulse-ring"
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: 'var(--bg-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1.5px solid var(--primary-accent)',
                  color: 'var(--primary-accent)',
                  marginBottom: 4,
                }}
              >
                <RefreshCw size={26} className="animate-spin" />
              </div>

              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>
                AI Đang Phân Tích & Ghép Nối Dữ Liệu...
              </div>

              <div style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 280, lineHeight: 1.4 }}>
                Đang nhận diện món bán & tự động loại bỏ các mục bị chụp trùng mép
              </div>

              {/* Animated Progress Scanner Bar */}
              <div className="progress-scanner-bar" />
            </div>
          )}

          <button
            onClick={() => {
              tg.haptic.impact('light');
              if (sales.length === 0) setSales([{ product_name: '', quantity: 1 }]);
              setStep(2);
            }}
            className="btn btn-secondary btn-full"
            style={{ fontSize: 13, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <PenLine size={16} /> Nhập liệu thủ công (không dùng ảnh)
          </button>

          <CameraCapture
            isOpen={isCameraOpen}
            onCapture={handleCameraCapture}
            onClose={() => setIsCameraOpen(false)}
          />
        </div>
      )}

      {/* STEP 2: Review & Edit Items in 3 Dedicated Sub-Tabs */}
      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {selectedImages.length > 0 && (
            <div className="card" style={{ padding: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {selectedImages.slice(0, 3).map((img, i) => (
                  <img
                    key={i}
                    src={img.base64}
                    alt={`Preview ${i + 1}`}
                    style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}
                  />
                ))}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-success)' }}>
                  ✓ Đã bóc tách từ {selectedImages.length} ảnh
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  Đã tự động loại bỏ các mục bị trùng lặp giữa các ảnh
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  tg.haptic.impact('medium');
                  const d = deduplicateLoadedItems(sales, rawStock, preparedStock);
                  setSales(d.sales);
                  setRawStock(d.raw);
                  setPreparedStock(d.prep);
                  if (d.totalMerged > 0) {
                    toast.success(`Đã tự động loại bỏ ${d.totalMerged} dòng bị trùng lặp!`);
                  } else {
                    toast('Không phát hiện dòng nào bị trùng lặp.', { icon: '✨' });
                  }
                }}
                className="btn btn-secondary"
                style={{
                  fontSize: 11,
                  padding: '5px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  color: 'var(--primary-accent)',
                  borderColor: 'var(--primary-accent)',
                  flexShrink: 0,
                }}
                title="Quét lại để lọc trùng lặp"
              >
                <Sparkles size={12} /> Lọc Trùng
              </button>
            </div>
          )}

          {/* Sub-Tab Navigation Bar */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 4,
              background: 'var(--bg-surface-subtle)',
              padding: 4,
              borderRadius: 'var(--radius-md)',
            }}
          >
            <button
              onClick={() => { tg.haptic.selectionChanged(); setActiveTab('SALES'); }}
              className={`btn ${activeTab === 'SALES' ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                fontSize: 11.5,
                minHeight: 40,
                padding: '4px 6px',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
              }}
            >
              <Coffee size={14} />
              <span>1. Món Bán ({sales.length})</span>
            </button>

            <button
              onClick={() => { tg.haptic.selectionChanged(); setActiveTab('RAW'); }}
              className={`btn ${activeTab === 'RAW' ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                fontSize: 11.5,
                minHeight: 40,
                padding: '4px 6px',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
              }}
            >
              <Scale size={14} />
              <span>2. NL Thô ({rawStock.length})</span>
            </button>

            <button
              onClick={() => { tg.haptic.selectionChanged(); setActiveTab('PREP'); }}
              className={`btn ${activeTab === 'PREP' ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                fontSize: 11.5,
                minHeight: 40,
                padding: '4px 6px',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
              }}
            >
              <Layers size={14} />
              <span>3. BTP Tồn ({preparedStock.length})</span>
            </button>
          </div>

          {/* TAB 1: Doanh số bán món nước (POS) */}
          {activeTab === 'SALES' && (
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="card-header" style={{ marginBottom: 4 }}>
                <span className="card-title">Món Bán Ra Trong Ca (Bill POS)</span>
                <button
                  onClick={() => {
                    tg.haptic.impact('light');
                    setIsAddSaleModalOpen(true);
                  }}
                  className="badge badge-success"
                  style={{ border: 'none', cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Plus size={12} /> Thêm món
                </button>
              </div>

              {sales.length === 0 ? (
                <div
                  style={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px dashed var(--border-color)',
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 12 }}>
                    Chưa có món bán nào. Bấm <b>"+ Thêm món"</b> để ghi nhận doanh số ca.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.impact('light');
                      setIsAddSaleModalOpen(true);
                    }}
                    className="btn btn-secondary"
                    style={{ fontSize: 13, margin: '0 auto', display: 'inline-flex' }}
                  >
                    <Plus size={15} /> Thêm Món Đã Bán
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sales.map((s, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) 78px 36px 32px',
                        gap: 8,
                        alignItems: 'center',
                        padding: '10px 12px',
                        background: 'var(--bg-surface-subtle)',
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      <ItemDropdown
                        value={s.product_name}
                        items={products.map((p) => ({
                          name: p.name,
                          category: p.category,
                        }))}
                        placeholder="Chọn món đồ uống..."
                        onChange={(selectedName) => {
                          setSales((prev) =>
                            prev.map((item, i) => (i === idx ? { ...item, product_name: selectedName } : item))
                          );
                        }}
                      />
                      <input
                        type="number"
                        className="input num"
                        min="1"
                        value={s.quantity}
                        onChange={(e) => {
                          const v = Math.max(1, parseInt(e.target.value) || 1);
                          setSales((prev) => prev.map((item, i) => (i === idx ? { ...item, quantity: v } : item)));
                        }}
                        style={{ textAlign: 'center', minHeight: 38, padding: '4px 6px' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center' }}>
                        ly
                      </span>
                      <button
                        onClick={() => setSales((prev) => prev.filter((_, i) => i !== idx))}
                        className="stepper-btn"
                        style={{ color: 'var(--color-danger)', width: 32, height: 32 }}
                        title="Xóa món"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Cân đo Nguyên liệu thô tồn (NL) */}
          {activeTab === 'RAW' && (
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="card-header" style={{ marginBottom: 4 }}>
                <div>
                  <span className="card-title">Cân Đo Nguyên Liệu Thô Tồn Cuối Ca</span>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    Cân lượng hạt cà phê, sữa, đường... còn trong bao/bịch
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={populateAllRawIngredients}
                    className="badge badge-warning"
                    style={{ border: 'none', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 3 }}
                    title="Nạp toàn bộ nguyên liệu quầy để kiểm"
                  >
                    <Sparkles size={11} /> Nạp danh mục NL
                  </button>
                  <button
                    onClick={() => {
                      tg.haptic.impact('light');
                      setStockModalConfig({ isOpen: true, type: 'RAW' });
                    }}
                    className="badge badge-success"
                    style={{ border: 'none', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 3 }}
                  >
                    <Plus size={11} /> Thêm
                  </button>
                </div>
              </div>

              {rawStock.length === 0 ? (
                <div
                  style={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px dashed var(--border-color)',
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 12 }}>
                    Chưa có số cân NL thô nào. Bấm <b>"Nạp danh mục NL"</b> để điền nhanh hoặc bấm thêm từng món.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.impact('light');
                      setStockModalConfig({ isOpen: true, type: 'RAW' });
                    }}
                    className="btn btn-secondary"
                    style={{ fontSize: 13, margin: '0 auto', display: 'inline-flex' }}
                  >
                    <Plus size={15} /> Thêm Cân NL Thô
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rawStock.map((r, idx) => {
                    const matchMeta = rawMaster.find(
                      (m) => toComparisonKey(m.name) === toComparisonKey(r.item_name) || m.name === r.item_name
                    );
                    const unit = matchMeta ? matchMeta.unit : r.unit || 'g';
                    return (
                      <div
                        key={idx}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) 85px 40px 32px',
                          gap: 8,
                          alignItems: 'center',
                          padding: '10px 12px',
                          background: 'var(--bg-surface-subtle)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <ItemDropdown
                          value={r.item_name}
                          items={rawMaster.map((m) => ({
                            name: m.name,
                            category: m.category,
                          }))}
                          placeholder="Chọn nguyên liệu thô..."
                          onChange={(selectedName) => {
                            const found = rawMaster.find(
                              (m) => toComparisonKey(m.name) === toComparisonKey(selectedName) || m.name === selectedName
                            );
                            setRawStock((prev) =>
                              prev.map((item, i) =>
                                i === idx
                                  ? { ...item, item_name: selectedName, unit: found ? found.unit : 'g' }
                                  : item
                              )
                            );
                          }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <input
                            type="number"
                            className="input num"
                            min="0"
                            step="any"
                            value={r.quantity}
                            onChange={(e) => {
                              const v = Math.max(0, parseFloat(e.target.value) || 0);
                              setRawStock((prev) => prev.map((item, i) => (i === idx ? { ...item, quantity: v } : item)));
                            }}
                            style={{ textAlign: 'center', minHeight: 38, padding: '4px 6px' }}
                          />
                          {/* Phím bấm nhanh đếm số lon/hộp/túi sữa nguyên */}
                          {(r.item_name.toLowerCase().includes('390g') ||
                            r.item_name.toLowerCase().includes('1kg') ||
                            r.item_name.toLowerCase().includes('1000g') ||
                            r.item_name.toLowerCase().includes('24 hộp') ||
                            r.item_name.toLowerCase().includes('24hộp') ||
                            r.item_name.toLowerCase().includes('hộp') ||
                            r.item_name.toLowerCase().includes('túi') ||
                            r.item_name.toLowerCase().includes('bịch')) && (
                            <div style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                              {(() => {
                                const is1kg =
                                  r.item_name.toLowerCase().includes('1kg') ||
                                  r.item_name.toLowerCase().includes('1000g') ||
                                  r.item_name.toLowerCase().includes('24 hộp') ||
                                  r.item_name.toLowerCase().includes('24hộp');
                                const stepVal = is1kg ? 1000 : 390;
                                const unitLabel = is1kg ? 'hộp' : (r.item_name.toLowerCase().includes('túi') ? 'túi' : 'lon');

                                return (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        tg.haptic.impact('light');
                                        setRawStock((prev) =>
                                          prev.map((item, i) =>
                                            i === idx ? { ...item, quantity: Math.max(0, item.quantity - stepVal) } : item
                                          )
                                        );
                                      }}
                                      style={{
                                        fontSize: '10px',
                                        padding: '1px 4px',
                                        borderRadius: '3px',
                                        border: '1px solid var(--border-color)',
                                        background: 'var(--bg-surface)',
                                        cursor: 'pointer',
                                      }}
                                      title={`Bớt 1 ${unitLabel} nguyên (${stepVal}g)`}
                                    >
                                      -1 {unitLabel}
                                    </button>
                                    <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>
                                      {Math.round(r.quantity / stepVal)} {unitLabel}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        tg.haptic.impact('light');
                                        setRawStock((prev) =>
                                          prev.map((item, i) =>
                                            i === idx ? { ...item, quantity: item.quantity + stepVal } : item
                                          )
                                        );
                                      }}
                                      style={{
                                        fontSize: '10px',
                                        padding: '1px 4px',
                                        borderRadius: '3px',
                                        border: '1px solid var(--border-color)',
                                        background: 'var(--bg-surface)',
                                        cursor: 'pointer',
                                      }}
                                      title={`Thêm 1 ${unitLabel} nguyên (${stepVal}g)`}
                                    >
                                      +1 {unitLabel}
                                    </button>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'center' }}>
                          {unit}
                        </span>
                        <button
                          onClick={() => setRawStock((prev) => prev.filter((_, i) => i !== idx))}
                          className="stepper-btn"
                          style={{ color: 'var(--color-danger)', width: 32, height: 32 }}
                          title="Xóa mục"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Cân đo Bán thành phẩm tồn (BTP) */}
          {activeTab === 'PREP' && (
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="card-header" style={{ marginBottom: 4 }}>
                <div>
                  <span className="card-title">Cân Đo Bán Thành Phẩm Đã Pha Còn Tồn</span>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    Cân đo lượng cốt cà phê, cốt trà... còn trong bình để quy đổi ra NL thô
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={populateAllPrepIngredients}
                    className="badge badge-warning"
                    style={{ border: 'none', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 3 }}
                    title="Nạp toàn bộ BTP của quán"
                  >
                    <Sparkles size={11} /> Nạp BTP
                  </button>
                  <button
                    onClick={() => {
                      tg.haptic.impact('light');
                      setStockModalConfig({ isOpen: true, type: 'PREPARED' });
                    }}
                    className="badge badge-success"
                    style={{ border: 'none', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 3 }}
                  >
                    <Plus size={11} /> Thêm
                  </button>
                </div>
              </div>

              {preparedStock.length === 0 ? (
                <div
                  style={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px dashed var(--border-color)',
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 12 }}>
                    Chưa có BTP nào. Bấm <b>"Nạp BTP"</b> để ghi nhận nhanh hoặc bấm thêm từng món.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.impact('light');
                      setStockModalConfig({ isOpen: true, type: 'PREPARED' });
                    }}
                    className="btn btn-secondary"
                    style={{ fontSize: 13, margin: '0 auto', display: 'inline-flex' }}
                  >
                    <Plus size={15} /> Thêm Cân BTP Tồn
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {preparedStock.map((p, idx) => {
                    const matchMeta = prepMaster.find(
                      (m) => toComparisonKey(m.name) === toComparisonKey(p.item_name) || m.name === p.item_name
                    );
                    const unit = matchMeta ? matchMeta.unit : p.unit || 'ml';
                    return (
                      <div
                        key={idx}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) 85px 40px 32px',
                          gap: 8,
                          alignItems: 'center',
                          padding: '10px 12px',
                          background: 'var(--bg-surface-subtle)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <ItemDropdown
                          value={p.item_name}
                          items={prepMaster.map((m) => ({
                            name: m.name,
                            category: m.category,
                          }))}
                          placeholder="Chọn bán thành phẩm..."
                          onChange={(selectedName) => {
                            const found = prepMaster.find(
                              (m) => toComparisonKey(m.name) === toComparisonKey(selectedName) || m.name === selectedName
                            );
                            setPreparedStock((prev) =>
                              prev.map((item, i) =>
                                i === idx
                                  ? { ...item, item_name: selectedName, unit: found ? found.unit : 'ml' }
                                  : item
                              )
                            );
                          }}
                        />
                        <input
                          type="number"
                          className="input num"
                          min="0"
                          step="any"
                          value={p.quantity}
                          onChange={(e) => {
                            const v = Math.max(0, parseFloat(e.target.value) || 0);
                            setPreparedStock((prev) => prev.map((item, i) => (i === idx ? { ...item, quantity: v } : item)));
                          }}
                          style={{ textAlign: 'center', minHeight: 38, padding: '4px 6px' }}
                        />
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'center' }}>
                          {unit}
                        </span>
                        <button
                          onClick={() => setPreparedStock((prev) => prev.filter((_, i) => i !== idx))}
                          className="stepper-btn"
                          style={{ color: 'var(--color-danger)', width: 32, height: 32 }}
                          title="Xóa mục"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Cấu hình Đối soát & Ngưỡng Chênh Lệch Chi Phí Bù Trừ */}
          <div
            style={{
              padding: '12px 14px',
              backgroundColor: 'var(--bg-surface)',
              borderRadius: 'var(--radius-md)',
              border: '1.5px solid #FCD34D',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#92400E', display: 'flex', alignItems: 'center', gap: 6 }}>
                🛡️ Kiểm soát dung sai chi phí bù trừ
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Dung sai khối lượng: <b>10%</b></span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Ngưỡng lệch chi phí cảnh báo:
                </label>
                <button
                  type="button"
                  onClick={() => setShowThresholdHelp(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '2px 4px',
                    cursor: 'pointer',
                    color: '#D97706',
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: 4,
                  }}
                  title="Nhấn để xem giải thích nguyên lý đối soát chi phí"
                >
                  <HelpCircle size={16} />
                </button>
              </div>

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="number"
                  step="1000"
                  min="0"
                  placeholder="30000"
                  value={financialThreshold}
                  onChange={(e) => {
                    const val = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0);
                    setFinancialThreshold(val);
                  }}
                  style={{
                    width: 95,
                    fontSize: 13,
                    fontWeight: 800,
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1.5px solid #F59E0B',
                    backgroundColor: '#FFFBEB',
                    color: '#92400E',
                    textAlign: 'right',
                  }}
                />
                <span style={{ fontSize: 12.5, fontWeight: 800, color: '#92400E' }}>đ</span>
              </div>
            </div>

            <div style={{ fontSize: 11, color: '#92400E', backgroundColor: '#FEF3C7', padding: '5px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 5, lineHeight: 1.4 }}>
              <Info size={14} style={{ flexShrink: 0, color: '#D97706' }} />
              <span>
                Chênh lệch giá trị tối đa cho phép giữa các nguyên liệu thay thế (Cài đặt: <b>{Number(financialThreshold || 0).toLocaleString('vi-VN')}đ</b>)
              </span>
            </div>
          </div>

          {/* Next to Confirmation Button */}
          <button
            onClick={goToConfirm}
            className="btn btn-primary btn-full"
            style={{ fontSize: 15, padding: '14px 20px', minHeight: 52 }}
          >
            <ArrowRight size={18} /> Xem & Xác Nhận Chốt Ca ({confirmItems.length} mục)
          </button>
        </div>
      )}

      {/* STEP 3: Confirmation Screen */}
      {step === 3 && (
        <ConfirmView
          title="Xác Nhận Biên Bản Chốt Ca"
          description="Kiểm tra chi tiết Doanh số bán ra & Số cân đo tồn quầy. Bạn có thể bấm biểu tượng thùng rác ở dòng bất kỳ để xóa bỏ mục thừa hoặc trùng lặp:"
          items={confirmItems}
          onConfirm={submitReconciliation}
          onBack={() => setStep(2)}
          onDeleteItem={handleDeleteConfirmRow}
          isSubmitting={isSubmitting}
          isDisabled={isDisabled || confirmItems.length === 0}
          confirmText="Chốt Ca & Tính Toán Tiêu Thụ NL"
        />
      )}

      {/* STEP 4: Detailed Results & Consumed Grams Breakdown & Low Stock Warnings */}
      {step === 4 && result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Summary Card */}
          <div
            className="card"
            style={{
              background: 'linear-gradient(135deg, #FAF7F2 0%, #F0E8DD 100%)',
              border: '1px solid var(--border-color)',
              boxShadow: '0 4px 16px rgba(111, 64, 37, 0.06)',
              color: 'var(--text-primary)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span className="badge badge-success">Đã Chốt & Cân Bằng Kho Ca</span>
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 6, color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                  Ca: #{result.shift_id}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Tổng món bán:</div>
                <div className="num" style={{ fontSize: 24, fontWeight: 800, color: 'var(--primary-accent)' }}>
                  {result.summary.total_sales_count} ly
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 'var(--radius-sm)',
                background: '#ECFDF5',
                border: '1px solid #A7F3D0',
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <CheckCircle2 size={20} color="#059669" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#065F46' }}>
                Đã quy đổi BTP & tính chính xác mức tiêu thụ cho {result.items.length} mặt hàng!
              </span>
            </div>
          </div>

          {/* LOW STOCK WARNINGS - Cảnh báo khi gần hết kho */}
          {result.alerts.length > 0 && (
            <div className="card" style={{ border: '1.5px solid var(--color-danger)', backgroundColor: '#FFF5F5' }}>
              <div className="card-header" style={{ marginBottom: 6 }}>
                <span className="card-title" style={{ color: 'var(--color-danger)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={18} /> Cảnh Báo Tồn Kho Quầy Sau Ca ({result.summary.low_stock_count} mục sắp hết)
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {result.alerts.map((alert, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: alert.includes('tồn kho thấp') ? '#991B1B' : 'var(--text-primary)',
                      padding: '8px 10px',
                      background: alert.includes('tồn kho thấp') ? '#FEE2E2' : 'var(--bg-surface-subtle)',
                      borderRadius: 'var(--radius-sm)',
                      borderLeft: alert.includes('tồn kho thấp') ? '4px solid #DC2626' : undefined,
                    }}
                  >
                    {alert}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* BẢNG TỔNG KẾT TIÊU THỤ TỪNG GAM NGUYÊN LIỆU (THE CORE GOAL) */}
          <div className="card">
            <div className="card-header" style={{ marginBottom: 8 }}>
              <div>
                <span className="card-title" style={{ fontSize: 15, fontWeight: 800 }}>
                  ⚖️ Chi Tiết Tiêu Thụ Từng Gam Nguyên Liệu
                </span>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                  Từ {result.summary.total_sales_count} ly bán ra &rarr; Khối lượng thực tế đã tiêu thụ trong ca
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(result.raw_consumptions || []).map((rc, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: 12,
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: rc.is_low_stock ? 'var(--color-danger-bg)' : 'var(--bg-surface-subtle)',
                    border: rc.is_low_stock ? '1.5px solid var(--color-danger)' : '1px solid var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  {/* Top row: Name & Badges */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, fontSize: 14 }}>{rc.ingredient_name}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {result.items.find((it) => it.ingredient_name === rc.ingredient_name)?.status === 'SWAP_SUSPICION_ALERT' && (
                        <span className="badge badge-warning" style={{ color: '#B45309', borderColor: '#F59E0B', background: '#FEF3C7' }}>
                          ⚠️ Nghi vấn tráo đổi
                        </span>
                      )}
                      {rc.is_low_stock ? (
                        <span className="badge badge-warning" style={{ color: '#DC2626', borderColor: '#FCA5A5' }}>
                          ⚠️ Sắp hết kho
                        </span>
                      ) : (
                        <span className="badge badge-success">Tồn an toàn</span>
                      )}
                    </div>
                  </div>

                  {/* 4-Column Grid: Consumed, Theoretical, Variance, Remaining */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 6,
                      fontSize: 11,
                      marginTop: 4,
                      background: 'rgba(255, 255, 255, 0.65)',
                      padding: '8px 10px',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--text-tertiary)' }}>TT tiêu thụ:</span>
                      <div className="num font-bold" style={{ fontSize: 13, color: 'var(--primary-accent)' }}>
                        {formatQty(rc.consumed_grams, rc.unit)}
                      </div>
                    </div>

                    <div>
                      <span style={{ color: 'var(--text-tertiary)' }}>ĐM bán ra:</span>
                      <div className="num font-bold" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {formatQty(rc.theoretical_grams, rc.unit)}
                      </div>
                    </div>

                    <div>
                      <span style={{ color: 'var(--text-tertiary)' }}>Lệch hao hụt:</span>
                      <div
                        className="num font-bold"
                        style={{
                          fontSize: 13,
                          color: rc.variance_grams > 0 ? 'var(--color-danger)' : 'var(--color-success)',
                        }}
                      >
                        {formatVariance(rc.variance_grams, rc.unit)}
                      </div>
                    </div>

                    <div>
                      <span style={{ color: 'var(--text-tertiary)' }}>Tồn còn lại:</span>
                      <div
                        className={`num font-bold ${rc.is_low_stock ? 'color-danger' : 'color-success'}`}
                        style={{ fontSize: 13 }}
                      >
                        {formatQty(rc.ending_stock, rc.unit)}
                      </div>
                    </div>
                  </div>

                  {/* Footnote about reverse BOM */}
                  {rc.from_prepared_equivalent && rc.from_prepared_equivalent > 0 && (
                    <div style={{ fontSize: 10.5, color: '#92400E', fontStyle: 'italic', background: '#FEF3C7', padding: '3px 8px', borderRadius: 4 }}>
                      💡 Bao gồm <b>+{formatQty(rc.from_prepared_equivalent, rc.unit)}</b> quy đổi từ BTP đã pha còn tồn trong bình
                    </div>
                  )}
                  {rc.is_low_stock && rc.safety_stock > 0 && (
                    <div style={{ fontSize: 10.5, color: '#B91C1C', fontWeight: 600 }}>
                      ⚠️ Dưới mức an toàn quầy ({formatQty(rc.safety_stock, rc.unit)}). Cần chuyển thêm từ Kho Tổng (L2)!
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* BẢNG BÁN THÀNH PHẨM TỒN (BTP) */}
          {result.items.some((it) => it.type === 'PREPARED') && (
            <div className="card">
              <div className="card-header" style={{ marginBottom: 6 }}>
                <span className="card-title" style={{ fontSize: 14 }}>
                  🫖 Bán Thành Phẩm Đã Pha Còn Tồn (BTP)
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {result.items
                  .filter((it) => it.type === 'PREPARED')
                  .map((prep, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 10px',
                        background: 'var(--bg-surface-subtle)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 12,
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>{prep.ingredient_name}</span>
                      <div className="num" style={{ fontWeight: 800, color: prep.low_stock ? 'var(--color-danger)' : 'var(--text-primary)' }}>
                        {formatQty(prep.ending_actual_stock, prep.unit)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Finish Button */}
          <button
            onClick={() => {
              tg.haptic.impact('medium');
              onSuccess();
            }}
            className="btn btn-secondary btn-full"
            style={{ minHeight: 48, fontSize: 15 }}
          >
            Quay Về Trang Chủ
          </button>
        </div>
      )}

      {/* Add Shift Sale Modal */}
      <AddShiftSaleModal
        isOpen={isAddSaleModalOpen}
        onClose={() => setIsAddSaleModalOpen(false)}
        products={products.map((p) => ({
          name: p.name,
          category: p.category,
        }))}
        onAdd={handleAddSale}
      />

      {/* Add Shift Stock Modal (RAW or PREPARED) */}
      <AddShiftStockModal
        isOpen={stockModalConfig.isOpen}
        onClose={() => setStockModalConfig((prev) => ({ ...prev, isOpen: false }))}
        type={stockModalConfig.type}
        ingredients={
          stockModalConfig.type === 'RAW'
            ? rawMaster.map((m) => ({ name: m.name, category: m.category, unit: m.unit }))
            : prepMaster.map((m) => ({ name: m.name, category: m.category, unit: m.unit }))
        }
        units={units}
        onAdd={handleAddStock}
      />

      {/* Help Modal: Giải thích Ngưỡng Lệch Chi Phí */}
      {showThresholdHelp && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setShowThresholdHelp(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: 420,
              width: '100%',
              backgroundColor: '#FFF',
              borderRadius: 'var(--radius-lg)',
              padding: 18,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              boxShadow: 'var(--shadow-lg)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 15, color: '#92400E' }}>
                <HelpCircle size={20} color="#D97706" />
                <span>Ngưỡng lệch chi phí bù trừ là gì?</span>
              </div>
              <button
                type="button"
                onClick={() => setShowThresholdHelp(false)}
                style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: 'var(--text-tertiary)' }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ margin: 0 }}>
                💡 <b>Nguyên lý đối soát tài chính:</b> Trong các nhóm nguyên liệu cho phép bù trừ (BTP), mỗi nguyên liệu thành phần có <b>đơn giá nhập khác nhau</b> (được quản lý minh bạch tại cột Giá Nhập trong Master Data). Khi pha chế lệch tỷ lệ thực tế, tổng chi phí giá vốn ca sẽ thay đổi.
              </p>
              <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', padding: '8px 10px', borderRadius: 6, fontSize: 12 }}>
                🟢 <b>Lệch trong ngưỡng:</b> Độ lệch chi phí &le; <b>{Number(financialThreshold || 0).toLocaleString('vi-VN')}đ</b> &rarr; Hệ thống ghi nhận bù trừ hợp lệ, không báo động giả.
              </div>
              <div style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', padding: '8px 10px', borderRadius: 6, fontSize: 12 }}>
                🔴 <b>Vượt ngưỡng cho phép:</b> Chi phí hao hụt các nguyên liệu giá cao làm lệch tổng tiền &gt; <b>{Number(financialThreshold || 0).toLocaleString('vi-VN')}đ</b> &rarr; Kích hoạt cảnh báo tài chính để Chủ quán kiểm tra nguy cơ thất thoát hoặc tráo đổi nguyên liệu trong ca.
              </div>
              <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-secondary)' }}>
                📌 <b>Cài đặt linh hoạt:</b>
                <br />• <b>Trong tab Quản trị:</b> Cài đặt ngưỡng chuẩn riêng cho từng công thức BTP (ví dụ BTP Ca Sữa, BTP Cốt Trà, BTP Siro...).
                <br />• <b>Tại màn hình Chốt ca:</b> Mặc định tự nạp từ Master Data; cho phép Chủ quán điều chỉnh nhanh theo quy mô từng ca.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowThresholdHelp(false)}
              className="btn btn-primary"
              style={{ width: '100%', marginTop: 6, padding: '10px 0', fontSize: 14 }}
            >
              Tôi Đã Hiểu
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
